import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { EnvelopeIcon, UsersIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePageLoading } from '@/app/components/page-loading.tsx';
import AdminRepTable from '@/app/components/reps/admin-rep-table.tsx';
import InviteRepDialog from '@/app/components/reps/invite-rep-dialog.tsx';
import RepresentativeSearchForm from '@/app/components/reps/representative-search-form.tsx';
import RepresentativeTable from '@/app/components/reps/representative-table.tsx';
import { exportRepresentativesToCsv } from '@/app/helpers/csv.helpers.ts';
import { useAuth } from '@/app/hooks/auth-provider';
import toast from '@/app/toast.ts';
import { StaffRoleEnum } from '@/app/types/staff-member.type.ts';
import { Heading } from '@/primitives/heading.tsx';
import SecurityCheck from '@/primitives/security.check.tsx';
import { useFetchSearchRepresentativesQuery } from '@/slices/representative-api-slice.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';

const RepresentativeList = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { data, isFetching } = useFetchSearchRepresentativesQuery(Object.fromEntries(searchParams));
  const [inviteRepOpen, setInviteRepOpen] = useState(false);
  const { showPageLoading, hidePageLoading } = usePageLoading();
  const repCount = data?.length ?? 0;

  useEffect(() => {
    (isFetching ? showPageLoading : hidePageLoading)('representative-list');
  }, [isFetching]);

  const handleExportToCsv = () => {
    if (!repCount) {
      toast.error('No representatives found for the current filters');
      return;
    }

    exportRepresentativesToCsv(data ?? [], 'representatives');
    toast.success(`Exported ${repCount} representative${repCount === 1 ? '' : 's'} to CSV`);
  };

  return (
    <>
      <Heading icon={<UsersIcon className='size-7' />}>Representatives</Heading>
      <div className='flex justify-between py-5'>
        <RepresentativeSearchForm />
        <div className='flex items-center gap-2'>
          <SecurityCheck
            allowedUserTypes={[UserTypeEnum.Admin, UserTypeEnum.StaffMember]}
            minimumStaffRole={StaffRoleEnum.SiteAdministrator}
          >
            <Button
              color='primary'
              variant='flat'
              onPress={handleExportToCsv}
              startContent={<ArrowDownTrayIcon className='size-5' />}
              isDisabled={!repCount}
            >
              Export to CSV
            </Button>
          </SecurityCheck>
          <Button
            color='primary'
            variant='solid'
            onPress={() => setInviteRepOpen(true)}
            startContent={<EnvelopeIcon className='size-5' />}
          >
            Invite New Rep
          </Button>
        </div>
      </div>
      {user?.userType === UserTypeEnum.Admin && <AdminRepTable data={data} />}
      {user?.userType === UserTypeEnum.StaffMember && <RepresentativeTable data={data} />}
      <InviteRepDialog isOpen={inviteRepOpen} setIsOpen={setInviteRepOpen} />
    </>
  );
};

export default RepresentativeList;
